import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase';
import { clearUserKeyCache } from '../utils/userKey';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setLoading(false);

      // Check for pending brewery invitations on login
      if (user?.email) {
        try {
          const functions = getFunctions();
          const acceptInvitation = httpsCallable(functions, 'acceptBreweryInvitation');
          const result = await acceptInvitation({});
          const data = result.data as any;
          if (data.accepted) {
            console.log(`Accepted brewery invitation for ${user.email}`);
          }
        } catch (err) {
          // Silently ignore - most users won't have pending invitations
          console.log('No pending brewery invitations or check failed');
        }
      }
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    // Use popup - works fine when called directly from click event
    const userCredential = await signInWithPopup(auth, provider);

    // Create user document in Firestore if it doesn't exist (first time sign-in)
    const userDocRef = doc(db, 'users', userCredential.user.uid);
    await setDoc(userDocRef, {
      email: userCredential.user.email?.toLowerCase() || '',
      createdAt: serverTimestamp(),
      unitSystem: 'Metric', // Default unit system
    }, { merge: true }); // Use merge to not overwrite if document already exists

    console.log(`Ensured user document exists for ${userCredential.user.email} (${userCredential.user.uid})`);
  };

  const signUp = async (email: string, password: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Create user document in Firestore
    const userDocRef = doc(db, 'users', userCredential.user.uid);
    await setDoc(userDocRef, {
      email: email.toLowerCase(),
      createdAt: serverTimestamp(),
      unitSystem: 'Metric', // Default unit system
    });

    console.log(`Created user document for ${email} (${userCredential.user.uid})`);
  };

  // Sent by our own Cloud Function rather than Firebase's mailer: Firebase
  // sends from firebaseapp.com (lands in spam) and stays silent for accounts
  // that sign in with Google, which have no password to reset. The function
  // always resolves the same way regardless of whether the account exists.
  const resetPassword = async (email: string) => {
    const requestReset = httpsCallable(getFunctions(), 'requestPasswordReset');
    await requestReset({ email });
  };

  const signOut = async () => {
    try {
      // Clear user key cache before signing out to prevent cache leakage
      clearUserKeyCache();

      // Preserve cookie consent preference before clearing
      const cookieConsent = localStorage.getItem('cookie_consent_given');

      // Clear any other cached data before signing out
      localStorage.clear();

      // Restore cookie consent preference
      if (cookieConsent) {
        localStorage.setItem('cookie_consent_given', cookieConsent);
      }

      // Sign out - Firestore will automatically clean up listeners
      await firebaseSignOut(auth);
    } catch (error) {
      // Suppress Firestore cleanup errors during logout (ERR_BLOCKED_BY_CLIENT, 400, etc.)
      // These happen when Firestore tries to close listeners but auth is already invalid
      console.log('[Auth] Signed out (cleanup errors suppressed)');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signInWithGoogle, signUp, resetPassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 